import { Transition } from "@headlessui/react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid"
import classNames from "classnames"
import { determineStatusBackgroundSubduedClass } from "components/DesignSystemUtils/determineStatusBackgroundSubduedClass"
import { determineStatusTextClass } from "components/DesignSystemUtils/determineStatusTextClass"
import { determineStatusTextSecondaryClass } from "components/DesignSystemUtils/determineStatusTextSecondaryClass"
import { StatusVariant } from "components/DesignSystemUtils/StatusVariant"
import { useTranslate } from "hooks/useTranslate"
import { FC, MouseEvent, ReactNode, useCallback, useEffect, useState } from "react"

export enum AlertPopupVariant {
  Info = "info",
  Success = "success",
  Warn = "warning",
  Error = "error",
}

export const ALERT_ICON_MAP: Record<StatusVariant, FC<{ className?: string }>> = {
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: ExclamationCircleIcon,
  neutral: InformationCircleIcon,
}

export function resolveAlertClasses(variant: StatusVariant): { containerClassName: string; iconClassName: string } {
  return {
    containerClassName: `${determineStatusBackgroundSubduedClass(variant)} ${determineStatusTextSecondaryClass(variant)}`,
    iconClassName: determineStatusTextClass(variant),
  }
}

interface Props {
  show?: boolean
  showIcon?: boolean
  closable?: boolean
  closeAfter?: number // Automatically closes after this many milliseconds
  closeKey?: string // If closed, alerts using this key will not be shown again during the session
  onClose?: () => void // Callback when the alert is closed
  variant?: StatusVariant
  className?: string
  preventWrap?: boolean // Prevents wrapping on mobile, keeping close button on same line
  children: ReactNode
}

/** Shows an alert popup. Can be sized (as opposed to a `Toast` message, which is fixed) */
export const Alert = ({
  show = true,
  showIcon = true,
  closable = false,
  closeAfter,
  closeKey,
  onClose,
  variant = "info",
  className,
  preventWrap = false,
  children,
}: Props) => {
  const t = useTranslate()
  const [isClosed, setIsClosed] = useState(() => {
    return closeKey ? sessionStorage.getItem(closeKey) : false
  })

  const { containerClassName, iconClassName } = resolveAlertClasses(variant)
  const Icon = ALERT_ICON_MAP[variant]

  const isVisible = show && !isClosed

  const handleClose = useCallback(() => {
    setIsClosed(true)
    if (closeKey) {
      sessionStorage.setItem(closeKey, "true")
    }
    onClose?.()
  }, [closeKey, onClose])

  useEffect(() => {
    if (closeAfter && show) {
      const timer = setTimeout(handleClose, closeAfter)
      return () => clearTimeout(timer)
    }
  }, [handleClose, closeAfter, show])

  const handleContainerClick = (event: MouseEvent<HTMLDivElement>) => {
    // Clicking anywhere in a closable alert dismisses it, but following a link or
    // pressing a button inside the message is not a dismiss gesture.
    const clickedInteractiveElement = event.target instanceof Element && !!event.target.closest("a, button")

    if (closable && !clickedInteractiveElement) {
      handleClose()
    }
  }

  return (
    <Transition
      show={isVisible}
      enter="transition-all duration-200"
      enterFrom="opacity-0 max-h-0 my-0"
      enterTo="opacity-100 max-h-40 sm:max-h-20 md:max-h-24 my-2"
      leave="transition-all duration-200"
      leaveFrom="opacity-100 max-h-40 sm:max-h-20 md:max-h-24 my-2"
      leaveTo="opacity-0 max-h-0 my-0"
    >
      <div
        className={classNames(
          "flex h-min w-full items-start justify-start gap-2 overflow-hidden rounded-md p-4 text-body1/regular",
          preventWrap ? "flex-nowrap" : "flex-wrap sm:flex-nowrap",
          closable && "cursor-pointer",
          containerClassName,
          className
        )}
        onClick={handleContainerClick}
      >
        {showIcon && <Icon className={classNames("size-6 shrink-0", iconClassName)} />}
        <div className="grow">{children}</div>
        {closable && (
          <div className="min-w-[12px]">
            <span className="sr-only">{t("button.close")}</span>
            <XMarkIcon className="size-5" />
          </div>
        )}
      </div>
    </Transition>
  )
}
